/**
 * MaybeEntityBadge — render `EntityBadgeById` when an entity id and orgId
 * are both available, otherwise render a static `EntityBadge` from the
 * supplied display data. Owns the `useMemo` so the state literal stays
 * referentially stable across re-renders.
 *
 * Lives in its own file so `registry.helpers.tsx` can stay HMR-friendly
 * under the `react-refresh/only-export-components` rule.
 */

import { useMemo } from 'react';

import {
  EntityBadge,
  EntityBadgeById,
  type EntityBadgeState,
} from '@/lib/crm/components/badges';

interface MaybeEntityBadgeProps {
  entityId: string | undefined;
  orgId: number | undefined;
  entityType: string;
  displayName: string;
}

export function MaybeEntityBadge({
  entityId,
  orgId,
  entityType,
  displayName,
}: MaybeEntityBadgeProps) {
  const state = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: { id: entityId ?? '', entityType, displayName },
    }),
    [entityId, entityType, displayName]
  );
  if (entityId && orgId != null) {
    return (
      <EntityBadgeById
        entityId={entityId}
        orgId={orgId}
        variant='inline'
        fallbackDisplayName={displayName}
        fallbackEntityType={entityType}
      />
    );
  }
  return <EntityBadge state={state} variant='inline' interactive={false} />;
}
